/*
REFERENCE DOCS
https://www.algolia.com/doc/ui-libraries/autocomplete/introduction/getting-started/
*/
import { liteClient as algoliasearch } from 'algoliasearch/lite';
import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js';
import '@algolia/autocomplete-theme-classic/dist/theme.min.css';
import { createLocalStorageRecentSearchesPlugin } from '@algolia/autocomplete-plugin-recent-searches';
import { createQuerySuggestionsPlugin } from '@algolia/autocomplete-plugin-query-suggestions';
import { createRedirectUrlPlugin } from '@algolia/autocomplete-plugin-redirect-url';
import { DEFAULT_ATTR_TO_RETRIEVE } from '../common/constant';

custom.algoliaAutocomplete = (function () {
  const indexName = (name) => {
    return algoliaShopify.config.index_prefix + name;
  };
  const buildSearchUrl = (query, facets) => {
    const idxName = indexName('products');
    const params = [
      encodeURIComponent(idxName + '[query]') + '=' + encodeURIComponent(query),
    ];
    if (facets) {
      Object.keys(facets).forEach((attr) => {
        const values = Array.isArray(facets[attr])
          ? facets[attr]
          : [facets[attr]];
        values.forEach((value, i) => {
          params.push(
            encodeURIComponent(
              idxName + '[refinementList][' + attr + '][' + i + ']',
            ) +
              '=' +
              encodeURIComponent(value),
          );
        });
      });
    }
    return '/search?' + params.join('&');
  };
  /**
   * custom.algoliaAutocomplete.init() method
   */
  const init = () => {
    const searchClient = algoliasearch(
      algoliaShopify.config.app_id,
      algoliaShopify.config.search_api_key,
    );
    const autocompleteContainer = document.getElementById(
      'algoliaAutocomplete',
    );

    const redirectUrlPlugin = createRedirectUrlPlugin({
      templates: {
        item({ html, state }) {
          return html`<a
            class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
            ><span
              class="icon-autocomplete-collection"
              aria-hidden="true"
            ></span
            >${custom.utils.titleCaseWithExceptions(state.query)}</a
          >`;
        },
      },
    });

    const querySuggestionsPlugin = createQuerySuggestionsPlugin({
      searchClient,
      indexName: indexName('products_query_suggestions'),
      limit: 4,
      categoryAttribute: [
        indexName('products'),
        'facets',
        'exact_matches',
        'product_type',
      ],
      transformSource({ source, state }) {
        return {
          ...source,
          getItemUrl({ item }) {
            const facets = item.__autocomplete_qsCategory
              ? { product_type: item.__autocomplete_qsCategory }
              : null;
            return buildSearchUrl(item.query, facets);
          },
          templates: {
            item({ item, html }) {
              const facets = item.__autocomplete_qsCategory
                ? { product_type: item.__autocomplete_qsCategory }
                : null;
              // Leave the typed portion unstyled and bold everything else
              // around it, wherever the match falls within the suggestion.
              const suggestionCapitalized =
                custom.utils.titleCaseWithExceptions(item.query);
              const suggestion = item.query;
              const userQuery = state.query;
              const matchStart = userQuery
                ? suggestion.toLowerCase().indexOf(userQuery.toLowerCase())
                : -1;
              const hasMatch = matchStart >= 0;
              const matchEnd = hasMatch ? matchStart + userQuery.length : 0;
              const beforePart = hasMatch
                ? suggestionCapitalized.slice(0, matchStart)
                : suggestionCapitalized;
              const matchedPart = hasMatch
                ? suggestionCapitalized.slice(matchStart, matchEnd)
                : '';
              const afterPart = hasMatch
                ? suggestionCapitalized.slice(matchEnd)
                : '';
              return html`<a
                class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
                href="${buildSearchUrl(item.query, facets)}"
                ><span
                  class="icon-autocomplete-search"
                  aria-hidden="true"
                ></span
                ><span
                  >${beforePart
                    ? html`<span class="utility-xsmall--emphasis"
                        >${beforePart}</span
                      >`
                    : null}${matchedPart ? html`${matchedPart}` : null}<span
                    class="utility-xsmall--emphasis"
                    >${afterPart}${item.__autocomplete_qsCategory
                      ? html`${'\u00A0'}in${'\u00A0'}
                        ${custom.utils.titleCaseWithExceptions(
                          item.__autocomplete_qsCategory,
                        )}`
                      : null}</span
                  ></span
                ></a
              >`;
            },
          },
        };
      },
    });

    const recentSearchesPlugin = createLocalStorageRecentSearchesPlugin({
      key: 'RECENT_SEARCHES',
      limit: 2,
      transformSource({ source }) {
        return {
          ...source,
          getItemUrl({ item }) {
            return buildSearchUrl(item.label);
          },
          templates: {
            header({ html }) {
              return html`<p
                class="utility-xsmall utility-xsmall--emphasis content-default my-4 tracking-3 uppercase"
              >
                Recent Searches
              </p>`;
            },
            item({ item, html }) {
              return html`<a
                class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
                href="${buildSearchUrl(item.label)}"
                ><span
                  class="icon-autocomplete-search"
                  aria-hidden="true"
                ></span
                >${item.label}</a
              >`;
            },
          },
        };
      },
    });

    // Track the last set of product IDs so we can skip Slick
    // destroy/re-init when the product list hasn't actually changed.
    let prevProductIds = '';

    const autocompleteSearch = autocomplete({
      container: autocompleteContainer,
      insights: true,
      detachedMediaQuery: 'none',
      openOnFocus: true,
      placeholder: 'Search for Products and Collections',
      plugins: [
        querySuggestionsPlugin,
        recentSearchesPlugin,
        redirectUrlPlugin,
      ],
      // Trigger a custom event when the query changes
      onStateChange({ state }) {
        $('#autocompleteSearchInput').val(state.query ? state.query : '');
      },
      shouldPanelOpen() {
        return true;
      },
      // Route collections/editorial to #algoliaAutocompleteResults,
      // products to #AutoCompleteResult — neither uses panelContainer
      render({ elements, html, render, state }) {
        const {
          suggestions,
          products,
          recentSearchesPlugin: recentSearches,
          redirectUrlPlugin: redirect,
        } = elements || {};
        const algoliaResultsEl = document.getElementById(
          'algoliaAutocompleteResults',
        );
        const productsEl = document.getElementById('AutoCompleteResult');

        // Check whether the products source returned any hits
        const productsCollection =
          state.collections &&
          state.collections.find(
            (c) => c.source && c.source.sourceId === 'products',
          );
        const hasProducts =
          productsCollection &&
          productsCollection.items &&
          productsCollection.items.length > 0;

        // Build a fingerprint of the current product hit IDs so we can
        // tell whether the carousel actually needs to be torn down.
        const currentProductIds = hasProducts
          ? productsCollection.items.map((i) => i.objectID).join(',')
          : '';
        const productsChanged = currentProductIds !== prevProductIds;
        prevProductIds = currentProductIds;

        // Only destroy the Slick instance when the product list changed;
        // hover-triggered re-renders should leave the carousel untouched.
        if (productsChanged) {
          const $prevList = $('#AutoCompleteResult .aa-List.slick-initialized');
          if ($prevList.length) {
            $prevList.slick('destroy');
          }
        }

        if (algoliaResultsEl) {
          const hasSuggestions =
            state.collections &&
            state.collections.some(function (c) {
              return (
                c.source &&
                (c.source.sourceId === 'suggestions' ||
                  c.source.sourceId === 'redirectUrlPlugin') &&
                c.items &&
                c.items.length > 0
              );
            });
          render(
            html`${hasSuggestions || state.query.length > 0
              ? html`<p
                  class="utility-xsmall utility-xsmall--emphasis content-default mb-0 tracking-3 uppercase"
                >
                  Search Suggestions
                </p>`
              : null}
            ${redirect || null}${suggestions || null}${!hasSuggestions &&
            state.query.length > 0
              ? html`<a
                  class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
                  href="${buildSearchUrl(state.query)}"
                  ><span
                    class="icon-autocomplete-search"
                    aria-hidden="true"
                  ></span
                  ><span
                    ><span class="utility-xsmall--emphasis"
                      >${custom.utils.titleCaseWithExceptions(
                        state.query,
                      )}</span
                    ></span
                  ></a
                >`
              : null}${recentSearches || null}`,
            algoliaResultsEl,
          );
        }
        // Only re-render products and re-init carousel when the hits changed
        if (productsChanged && productsEl) {
          if (hasProducts) {
            const viewAllUrl = buildSearchUrl(state.query);
            render(
              html`
                ${products || null}
                ${state.query.length > 0
                  ? html`<a
                      href="${viewAllUrl}"
                      class="js-autocomplete-view-all-results btn mx-auto mt-4"
                    >
                      View All Results
                    </a>`
                  : null}
              `,
              productsEl,
            );
            const gtmData = {
              ecommerce: {
                currency: 'USD',
                item_list_name: 'Autocomplete Search Results',
                item_list_id: state.query,
                items: productsCollection.items.map((hit, index) => {
                  return {
                    item_id: hit.id ? hit.id.toString() : '',
                    item_name: hit.title ? hit.title : hit.name || '',
                    item_brand:
                      hit.named_tags && hit.named_tags.Collection
                        ? hit.named_tags.Collection
                        : '',
                    item_collaboration:
                      hit.named_tags && hit.named_tags.Collaboration
                        ? hit.named_tags.Collaboration
                        : '',
                    item_color:
                      hit.named_tags && hit.named_tags.Color
                        ? hit.named_tags.Color.toString()
                        : '',
                    index: index + 1,
                  };
                }),
              },
            };
            custom.gtmDatalayer.gtmPushNew(gtmData, 'view_item_list');
          } else {
            render(null, productsEl);
          }

          // Initialize Slick carousel after Preact updates the DOM
          setTimeout(() => {
            const $list = $('#AutoCompleteResult .aa-List');
            if ($list.length && $list.find('.aa-Item').length > 0) {
              $list.slick({
                infinite: false,
                dots: false,
                arrows: true,
                slidesToShow: 4,
                slidesToScroll: 2,
                prevArrow: '.js-autocomplete-product-carousel-arrows-prev',
                nextArrow: '.js-autocomplete-product-carousel-arrows-next',
                responsive: [
                  {
                    breakpoint: 1024,
                    settings: {
                      slidesToShow: 3,
                      slidesToScroll: 1,
                    },
                  },
                  {
                    breakpoint: 768,
                    settings: {
                      slidesToShow: 2,
                      slidesToScroll: 1,
                    },
                  },
                ],
              });
            }
            if (custom.productCardPillow) {
              custom.productCardPillow.init();
            }
          }, 0);
        }
      },
      onSubmit({ state, event }) {
        if (state.query.length > 0) {
          // Save to recent searches before navigating
          if (recentSearchesPlugin.data) {
            recentSearchesPlugin.data.addItem({
              id: state.query,
              label: state.query,
            });
          }
          // Prevent default form submission
          event.preventDefault();
          $('#autocompleteSearchForm').trigger('submit');
        }
      },
      // Autocomplete sources
      getSources({ query }) {
        return [
          {
            sourceId: 'pages',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: indexName('pages'),
                    params: {
                      query,
                      ruleContexts: ['enable-redirect-url'], // triggers rules configured only with this context
                      hitsPerPage: 2,
                    },
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return `/pages/${item.handle}`;
            },
            templates: {
              item({ item, html }) {
                return html`<a
                  class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
                  href="/pages/${item.handle}"
                  ><span
                    class="icon-autocomplete-collection"
                    aria-hidden="true"
                  ></span
                  >${custom.utils.titleCaseWithExceptions(item.title)}</a
                >`;
              },
            },
          },
          {
            sourceId: 'collections',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: indexName('collections'),
                    params: {
                      query,
                      hitsPerPage: 3,
                    },
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return `/collections/${item.handle}`;
            },
            templates: {
              item({ item, html }) {
                let displayTitle = item.title;
                const loloiMeta =
                  item.meta && item.meta.loloi ? item.meta.loloi : null;
                if (
                  loloiMeta &&
                  loloiMeta.collection_type !== 'Collaboration'
                ) {
                  if (loloiMeta.collection_type === 'Product Category') {
                    // displayTitle = 'Category: ' + displayTitle;
                  } else if (
                    loloiMeta.collaboration &&
                    loloiMeta.collaboration.length > 0
                  ) {
                    displayTitle = displayTitle + ' Collection';
                  }
                }
                return html`<a
                  class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
                  href="/collections/${item.handle}"
                  ><span
                    class="icon-autocomplete-collection"
                    aria-hidden="true"
                  ></span
                  >${custom.utils.titleCaseWithExceptions(displayTitle)}</a
                >`;
              },
            },
          },
          {
            sourceId: 'editorial',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: indexName('articles'),
                    params: {
                      query,
                      hitsPerPage: 3,
                    },
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return `/blog/${item.blog.handle}/${item.handle}`;
            },
            templates: {
              item({ item, html }) {
                return html`<a
                  class="utility-xsmall content-subtle tracking-075 flex items-center gap-2 hover:surface-subtle p-2"
                  href="/blogs/${item.blog.handle}/${item.handle}"
                  ><span
                    class="icon-autocomplete-collection"
                    aria-hidden="true"
                  ></span
                  >${item.title}</a
                >`;
              },
            },
          },
          {
            sourceId: 'products',
            getItems() {
              return getAlgoliaResults({
                searchClient,
                queries: [
                  {
                    indexName: indexName('products--autocomplete'),
                    params: {
                      query,
                      hitsPerPage: 8,
                      distinct: true,
                      attributesToRetrieve: [...DEFAULT_ATTR_TO_RETRIEVE],
                    },
                  },
                ],
              });
            },
            getItemUrl({ item }) {
              return `/products/${item.handle}`;
            },
            onSelect({ item, state }) {
              if (!item.id) return;
              const data = {
                ecommerce: {
                  currency: 'USD',
                  item_list_id: state.query,
                  item_list_name: 'Autocomplete Search Results',
                  items: [
                    {
                      item_id: item.id.toString(),
                      item_name: item.title ? item.title : item.name || '',
                      item_brand:
                        item.named_tags && item.named_tags.Collection
                          ? item.named_tags.Collection
                          : '',
                      item_collaboration:
                        item.named_tags && item.named_tags.Collaboration
                          ? item.named_tags.Collaboration
                          : '',
                      item_color:
                        item.named_tags && item.named_tags.Color
                          ? item.named_tags.Color.toString()
                          : '',
                    },
                  ],
                },
              };
              custom.gtmDatalayer.gtmPushNew(data, 'view_item');
            },
            templates: {
              header({ html }) {
                return html`<div class="flex items-center mb-4">
                  <p
                    class="utility-xsmall utility-xsmall--emphasis content-default tracking-3 uppercase flex-1"
                  >
                    Product Suggestions
                  </p>
                  <div class="card-carousel__arrows">
                    <button
                      class="slick-prev slick-arrow js-autocomplete-product-carousel-arrows-prev"
                      aria-label="Previous"
                      type="button"
                      title="Previous"
                    >
                      <svg
                        width="32"
                        height="33"
                        viewBox="0 0 32 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle class="circle" cx="16" cy="16.8002" r="15.5" />
                        <path
                          class="arrow"
                          d="M19.1992 24.0006L11.9992 16.8006L19.1992 9.60065"
                          stroke-linecap="round"
                        />
                      </svg></button
                    ><button
                      class="slick-next slick-arrow js-autocomplete-product-carousel-arrows-next ml-2"
                      aria-label="Next"
                      type="button"
                      title="Next"
                    >
                      <svg
                        width="32"
                        height="33"
                        viewBox="0 0 32 33"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle class="circle" cx="16" cy="16.75" r="15.5" />
                        <path
                          class="arrow"
                          d="M11.999 9.55117L19.199 16.7512L11.999 23.9512"
                          stroke-linecap="round"
                        />
                      </svg>
                    </button>
                  </div>
                </div>`;
              },
              item({ item, html, state }) {
                const productsCollection =
                  state.collections &&
                  state.collections.find(
                    (c) => c.source && c.source.sourceId === 'products',
                  );
                const itemIndex = productsCollection
                  ? productsCollection.items.indexOf(item) + 1
                  : 0;
                return custom.algoliaUtils.autocompleteProductCardHtml(
                  item,
                  html,
                  state.query,
                  itemIndex,
                );
              },
              noResults({ html }) {
                return html`<p
                  class="utility-xsmall utility-xsmall--emphasis content-subtle tracking-075"
                >
                  No Products Found
                </p>`;
              },
            },
          },
        ];
      },
      // Merge pages/collections/editorial/querySuggestions into a single
      // 'suggestions' source sorted by relevance, capped at 8 total.
      // A single source means the render sees items in true score order
      // rather than grouped by source type.
      reshape({ sources, state }) {
        const suggestionIds = [
          'pages',
          'collections',
          'editorial',
          'querySuggestionsPlugin',
        ];
        const limit = 6;

        const suggestionSources = sources.filter(
          (s) => suggestionIds.indexOf(s.sourceId) !== -1,
        );
        const otherSources = sources.filter(
          (s) => suggestionIds.indexOf(s.sourceId) === -1,
        );

        // Build a lookup so the merged source can delegate URL/template rendering
        // back to whichever original source produced each item.
        const sourceMap = {};
        suggestionSources.forEach((source) => {
          sourceMap[source.sourceId] = source;
        });

        // Cache items and record how many each source originally returned.
        const ids = [
          'pages',
          'collections',
          'querySuggestionsPlugin',
          'editorial',
        ];
        const itemsCache = {};
        const originalCounts = {};
        ids.forEach((id) => {
          // No query: show only querySuggestionsPlugin; with query: show all sources
          const skip =
            state.query.length === 0 && id !== 'querySuggestionsPlugin';
          itemsCache[id] =
            !skip && sourceMap[id] ? sourceMap[id].getItems() : [];
          originalCounts[id] = itemsCache[id].length;
        });

        // Start with all available items, then shed from lower-priority sources
        // until the total fits within the limit.
        // Reduction order (first shed = lowest priority in display):
        //   editorial (min 1) → querySuggestions (min 3) → pages (min 2) → collections (min 2)
        // A source is only reduced if it originally had items; its floor is its minimum.
        const counts = { ...originalCounts };
        const reductions = [
          { id: 'editorial', min: 1 },
          { id: 'querySuggestionsPlugin', min: 3 },
          { id: 'pages', min: 1 },
          { id: 'collections', min: 1 },
        ];
        reductions.forEach(({ id, min }) => {
          const total =
            counts.pages +
            counts.collections +
            counts.querySuggestionsPlugin +
            counts.editorial;
          if (total <= limit) return;
          if (originalCounts[id] === 0) return;
          const excess = total - limit;
          const canRemove = Math.max(0, counts[id] - min);
          counts[id] -= Math.min(canRemove, excess);
        });

        // Assemble in display order: pages → collections → querySuggestions → editorial
        const mergedItems = [];
        ids.forEach((id) => {
          itemsCache[id].slice(0, counts[id]).forEach((item) => {
            mergedItems.push({ ...item, __suggestionSourceId: id });
          });
        });

        const mergedSource = {
          sourceId: 'suggestions',
          getItems: () => mergedItems,
          getItemInputValue: ({ item, state }) => {
            const orig = sourceMap[item.__suggestionSourceId];
            return orig && orig.getItemInputValue
              ? orig.getItemInputValue({ item, state })
              : state.query;
          },
          getItemUrl: ({ item }) => {
            const orig = sourceMap[item.__suggestionSourceId];
            return orig && orig.getItemUrl ? orig.getItemUrl({ item }) : '#';
          },
          onActive: (params) => {
            const orig = sourceMap[params.item.__suggestionSourceId];
            if (orig && orig.onActive) orig.onActive(params);
          },
          onSelect: (params) => {
            const orig = sourceMap[params.item.__suggestionSourceId];
            if (orig && orig.onSelect) orig.onSelect(params);
          },
          templates: {
            item({ item, html }) {
              const orig = sourceMap[item.__suggestionSourceId];
              return orig && orig.templates && orig.templates.item
                ? orig.templates.item({ item, html })
                : null;
            },
          },
        };

        return [mergedSource, ...otherSources];
      },
      // Default Navigator API implementation
      navigator: {
        navigate({ itemUrl }) {
          window.location.assign(itemUrl);
        },
        navigateNewTab({ itemUrl }) {
          const windowReference = window.open(itemUrl, '_blank', 'noopener');

          if (windowReference) {
            windowReference.focus();
          }
        },
        navigateNewWindow({ itemUrl }) {
          window.open(itemUrl, '_blank', 'noopener');
        },
      },
    });
  };

  // Make public
  return {
    init,
  };
})();



// WEBPACK FOOTER //
// ./src/scripts/src/custom/algolia-autocomplete.js